import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Icon, ICONS } from 'components/Icons';
import ToolTip from 'components/elements/atoms/ToolTip/ToolTip';
import * as syllabusApi from 'services/syllabusApi';
import {
  formatSyllabusDate,
  parseSyllabusDate,
} from 'routes/Portfolio/Component/CoursesTab/Topic/components/utils/dateHelper';
import DescriptionModal from './DescriptionModal';
import ConfirmModal from 'components/modals/Confirm/ConfirmModal';
import classNames from 'classnames';
import ActionLink from 'components/elements/molecules/ActionLink/ActionLink';
import InvalidLectureTopicPopover from './InvalidLectureTopicPopover';

const DESCRIPTION_PREVIEW_LIMIT = 1000;
const DESCRIPTION_INLINE_EDIT_LIMIT = 200;

let tempIdCounter = 0;
const nextTempId = () => `_temp_${++tempIdCounter}`;

const getStableId = (row) => row._id || row._tempId;

const SyllabusScheduleTable = forwardRef(function SyllabusScheduleTable(
  {
    rows,
    onChange,
    courseId,
    syllabusData,
    onHasUnsavedChanges,
    onEffectiveRowsChange,
  },
  ref,
) {
  // { [stableId]: { date?, dueDate?, topic?, description? } }
  const [pendingChanges, setPendingChanges] = useState({});
  const [editingCell, setEditingCell] = useState(null); // { stableId, field }
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [descriptionModalIndex, setDescriptionModalIndex] = useState(null);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const editingCellRef = useRef(editingCell);
  editingCellRef.current = editingCell;
  const skipBlurRef = useRef(false);

  const hasPending = Object.keys(pendingChanges).length > 0;

  const computeEffectiveRows = () =>
    rows.map((row) => {
      const sid = getStableId(row);
      const pending = pendingChanges[sid];
      if (!pending || Object.keys(pending).length === 0) return row;
      const newTopic =
        'topic' in pending ? pending.topic : row.topic || row.name || '';
      return {
        ...row,
        date: 'date' in pending ? pending.date : row.date,
        dueDate: 'dueDate' in pending ? pending.dueDate : row.dueDate,
        topic: newTopic,
        name: newTopic,
        description:
          'description' in pending ? pending.description : row.description,
      };
    });

  useEffect(() => {
    onHasUnsavedChanges?.(hasPending);
  }, [hasPending]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onEffectiveRowsChange?.(computeEffectiveRows());
  }, [pendingChanges, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastFocusedTempIdRef = useRef(null);

  useEffect(() => {
    const lastTempRow = [...rows].reverse().find((r) => r._tempId);
    if (!lastTempRow) return;
    const sid = getStableId(lastTempRow);
    if (lastFocusedTempIdRef.current === sid) return;
    if (editingCellRef.current?.stableId === sid) return;
    const pending = pendingChanges[sid] || {};
    const hasDate = !!('date' in pending ? pending.date : normalizeComparableDate(lastTempRow.date));
    const hasTopic = !!(('topic' in pending ? pending.topic : (lastTempRow.topic || lastTempRow.name || '')) || '').trim();
    if (hasDate && hasTopic) return;
    lastFocusedTempIdRef.current = sid;
    setEditingCell({ stableId: sid, field: 'date' });
    setEditValue('');
  }, [rows, pendingChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizeComparableDate = (date) => {
    const parsed = parseSyllabusDate(date);
    return parsed ? parsed.format('YYYY-MM-DD') : '';
  };

  const hasEditedTopicOrDate = (row, { date, dueDate, topic }) => {
    return (
      (row.topic || row.name || '').trim() !== (topic || '').trim() ||
      normalizeComparableDate(row.date) !== normalizeComparableDate(date) ||
      normalizeComparableDate(row.dueDate) !== normalizeComparableDate(dueDate)
    );
  };

  const getDescriptionPreview = (description = '') => {
    if (description.length <= DESCRIPTION_PREVIEW_LIMIT) return description;
    return description.slice(0, DESCRIPTION_PREVIEW_LIMIT);
  };

  const openDescriptionModal = (index) => {
    setDescriptionModalIndex(index);
    setDescriptionModalOpen(true);
  };

  // Returns the value that should be displayed for a cell (pending override or original)
  const getEffectiveValue = (row, field) => {
    const sid = getStableId(row);
    const pending = pendingChanges[sid];
    if (pending && field in pending) return pending[field];
    switch (field) {
      case 'date':
        return normalizeComparableDate(row.date);
      case 'dueDate':
        return normalizeComparableDate(row.dueDate);
      case 'topic':
        return row.topic || row.name || '';
      case 'description':
        return row.description || '';
      default:
        return '';
    }
  };

  const getOriginalValue = (row, field) => {
    switch (field) {
      case 'date':
        return normalizeComparableDate(row.date);
      case 'dueDate':
        return normalizeComparableDate(row.dueDate);
      case 'topic':
        return row.topic || row.name || '';
      case 'description':
        return row.description || '';
      default:
        return '';
    }
  };

  const isCellDirty = (row, field) => {
    const sid = getStableId(row);
    const pending = pendingChanges[sid];
    return !!(pending && field in pending);
  };

  const handleCellClick = (e, row, field) => {
    e.stopPropagation();
    const sid = getStableId(row);
    if (editingCell?.stableId === sid && editingCell?.field === field) return;
    setEditingCell({ stableId: sid, field });
    setEditValue(getEffectiveValue(row, field));
    setError('');
  };

  const handleCellBlur = (row, field) => {
    const sid = getStableId(row);
    const valueToSave = editValue;
    setEditingCell(null);

    // if (field === 'topic' && !(valueToSave || '').trim()) {
    //   setError('Lecture topic is required.');
    //   return;
    // }

    const originalValue = getOriginalValue(row, field);
    const normalizedValue =
      field === 'topic' || field === 'description'
        ? (valueToSave || '').trim()
        : valueToSave;

    setPendingChanges((prev) => {
      const existing = prev[sid] || {};
      const fieldChanged = normalizedValue !== originalValue;

      if (!fieldChanged) {
        // No change from original — remove this field from pending
        const { [field]: _removed, ...rest } = existing;
        if (Object.keys(rest).length === 0) {
          const { [sid]: _removedRow, ...remaining } = prev;
          return remaining;
        }
        return { ...prev, [sid]: rest };
      }

      return { ...prev, [sid]: { ...existing, [field]: normalizedValue } };
    });

    if (
      row._tempId &&
      !row.checkValidity &&
      (field === 'date' || field === 'topic')
    ) {
      const newDate =
        field === 'date' ? normalizedValue : getEffectiveValue(row, 'date');
      const newTopic =
        field === 'topic' ? normalizedValue : getEffectiveValue(row, 'topic');
      if (newDate && newTopic) {
        onChange(
          rows.map((r) =>
            getStableId(r) === sid ? { ...r, checkValidity: true } : r,
          ),
        );
      }
    }
  };

  // Called by the parent when the Save button is clicked.
  // Saves all pending changes to the API, then calls onChange with the merged result.
  const saveAll = async () => {
    const pendingIds = Object.keys(pendingChanges);
    if (pendingIds.length === 0) return 0;

    const updatedRows = [...rows];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const sid = getStableId(row);
      const pending = pendingChanges[sid];
      if (!pending || Object.keys(pending).length === 0) continue;

      const newDate =
        'date' in pending ? pending.date : normalizeComparableDate(row.date);
      const newDueDate =
        'dueDate' in pending
          ? pending.dueDate
          : normalizeComparableDate(row.dueDate);
      const newTopic =
        'topic' in pending ? pending.topic : row.topic || row.name || '';
      const newDescription =
        'description' in pending ? pending.description : row.description || '';

      const updatedRow = {
        date: newDate,
        dueDate: newDueDate,
        name: newTopic,
        description: newDescription,
        ...(row.invalidLectureTopic &&
        hasEditedTopicOrDate(row, {
          date: newDate,
          dueDate: newDueDate,
          topic: newTopic,
        })
          ? { invalidLectureTopic: false }
          : {}),
        ...(row.invalidLectureDescription &&
        'description' in (pendingChanges[sid] || {})
          ? { invalidLectureDescription: false }
          : {}),
      };

      if (row._id) {
        await syllabusApi.updateSyllabusRow(courseId, row._id, updatedRow);
      } else {
        const result = await syllabusApi.addSyllabusRow(courseId, updatedRow);
        updatedRow._id = result._id || result.rowId;
      }

      updatedRows[i] = {
        ...row,
        ...updatedRow,
        topic: newTopic,
      };
    }

    onChange(updatedRows);
    setPendingChanges({});
    return pendingIds.length;
  };

  useImperativeHandle(ref, () => ({ saveAll, hasUnsavedChanges: hasPending }));

  const handleDescriptionSave = (saveData) => {
    const index = descriptionModalIndex;
    const row = rows[index];
    const sid = getStableId(row);
    const newTopic = (saveData.topic || row.topic || row.name || '').trim();
    const newDate = saveData.date || row.date || '';
    const newDescription = (saveData.description || '').trim();

    setPendingChanges((prev) => {
      const existing = prev[sid] || {};
      const updates = { ...existing };

      const originalTopic = getOriginalValue(row, 'topic');
      const originalDate = getOriginalValue(row, 'date');
      const originalDescription = getOriginalValue(row, 'description');

      if (newTopic !== originalTopic) updates.topic = newTopic;
      else delete updates.topic;

      if (normalizeComparableDate(newDate) !== originalDate)
        updates.date = normalizeComparableDate(newDate);
      else delete updates.date;

      if (newDescription !== originalDescription)
        updates.description = newDescription;
      else delete updates.description;

      if (Object.keys(updates).length === 0) {
        const { [sid]: _removed, ...remaining } = prev;
        return remaining;
      }
      return { ...prev, [sid]: updates };
    });

    setError('');
  };

  const deleteRow = (index) => {
    setDeleteConfirmIndex(index);
  };

  const confirmDelete = async (confirmed) => {
    const index = deleteConfirmIndex;
    setDeleteConfirmIndex(null);
    if (!confirmed) return;

    const row = rows[index];
    const sid = getStableId(row);

    if (editingCell?.stableId === sid) setEditingCell(null);

    try {
      if (row._id) {
        await syllabusApi.deleteSyllabusRow(courseId, row._id);
      }
      onChange(rows.filter((_, i) => i !== index));
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[sid];
        return next;
      });
      setError('');
    } catch (err) {
      console.error('Error deleting row:', err);
      setError('Failed to delete row. Please try again.');
    }
  };

  const addRow = () => {
    setError('');
    const tempId = nextTempId();
    const newRow = {
      _tempId: tempId,
      date: '',
      dueDate: '',
      weekStartDate: '',
      topic: '',
      description: '',
      checkValidity: false,
    };
    onChange([...rows, newRow]);
  };

  const formatDate = (date) => formatSyllabusDate(date, 'MM/DD/YYYY');
  const formatDueDate = (dueDate) => formatSyllabusDate(dueDate, 'MM/DD/YYYY');

  const visibleRows =
    syllabusData?.enabled === true
      ? rows.filter((row) => !row.skipWeek)
      : rows;

  const hasInvalidLectureTopics = visibleRows.some(
    (row) => row?.invalidLectureTopic === true,
  );

  return (
    <>
      {deleteConfirmIndex !== null && (
        <ConfirmModal
          model={{ message: 'Are you sure you want to delete this row?' }}
          onClose={confirmDelete}
        />
      )}
      <DescriptionModal
        show={descriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        value={
          descriptionModalIndex !== null
            ? getEffectiveValue(rows[descriptionModalIndex], 'description')
            : ''
        }
        lectureTopic={
          descriptionModalIndex !== null
            ? getEffectiveValue(rows[descriptionModalIndex], 'topic')
            : ''
        }
        lectureDate={
          descriptionModalIndex !== null
            ? getEffectiveValue(rows[descriptionModalIndex], 'date')
            : ''
        }
        readingsScheduleDate={
          descriptionModalIndex !== null
            ? rows[descriptionModalIndex]?.weekStartDate || ''
            : ''
        }
        onSave={handleDescriptionSave}
        title="Edit Description"
        showLectureFields
      />
      <div className="syllabus-table-wrapper">
        {error && <div className="syllabus-error mb-2">{error}</div>}
        <table className="editable-table">
          <thead>
            <tr>
              <th>Lecture Date</th>
              <th>Lecture topic</th>
              <th>Due Date</th>
              <th>Description</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const originalIndex = rows.indexOf(row);
              const sid = getStableId(row);
              const invalidLectureTopicReason =
                row.invalidLectureTopic && row.invalidLectureTopicReason
                  ? row.invalidLectureTopicReason
                  : '';
              const invalidLectureDescriptionReason =
                row.invalidLectureDescription &&
                row.invalidLectureDescriptionReason
                  ? row.invalidLectureDescriptionReason
                  : '';

              const isEditingDate =
                editingCell?.stableId === sid && editingCell?.field === 'date';
              const isEditingTopic =
                editingCell?.stableId === sid && editingCell?.field === 'topic';
              const isEditingDueDate =
                editingCell?.stableId === sid &&
                editingCell?.field === 'dueDate';
              const isEditingDescription =
                editingCell?.stableId === sid &&
                editingCell?.field === 'description';

              const effectiveDate = getEffectiveValue(row, 'date');
              const effectiveDueDate = getEffectiveValue(row, 'dueDate');
              const effectiveTopic = getEffectiveValue(row, 'topic');
              const effectiveDescription = getEffectiveValue(
                row,
                'description',
              );

              return (
                <tr
                  key={sid}
                  className={classNames({
                    'is-editing-row': editingCell?.stableId === sid,
                  })}
                  title={
                    invalidLectureTopicReason
                      ? invalidLectureTopicReason +
                        " We're not sure what readings would be relevant."
                      : ''
                  }
                >
                  {/* Lecture Date */}
                  <td>
                    <div
                      className={classNames('syllabus-date-cell', {
                        'missing-date':
                          !effectiveDate &&
                          !effectiveDueDate &&
                          !row.invalidLectureTopic,
                        'temp-missing-date': !!row._tempId && !effectiveDate && editingCell?.stableId === sid,
                        'is-editing-cell': isEditingDate,
                        'cell-dirty': isCellDirty(row, 'date'),
                        'editable-cell': !row.invalidLectureTopic,
                      })}
                      style={{ minHeight: '35px' }}
                      onClick={(e) =>
                        !row.invalidLectureTopic &&
                        handleCellClick(e, row, 'date')
                      }
                    >
                      {isEditingDate ? (
                        <input
                          type="date"
                          className="syllabus-input"
                          value={editValue}
                          autoFocus
                          onFocus={(e) => e.target.showPicker?.()}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => {
                            if (skipBlurRef.current) {
                              skipBlurRef.current = false;
                              return;
                            }
                            handleCellBlur(row, 'date');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setEditingCell(null);
                              setEditValue('');
                            }
                            if (e.key === 'Enter') {
                              handleCellBlur(row, 'date');

                              if (!effectiveTopic) {
                                skipBlurRef.current = true;
                                const sid = getStableId(row);
                                setEditingCell({
                                  stableId: sid,
                                  field: 'topic',
                                });
                                setEditValue('');
                              }
                            }
                          }}
                        />
                      ) : effectiveDate || effectiveDueDate ? (
                        <div
                          className={
                            row.invalidLectureTopic
                              ? 'syllabus-invalid-lecture-text d-flex align-items-center'
                              : 'd-flex align-items-center'
                          }
                          style={{ minHeight: '35px', padding: '4px 8px' }}
                        >
                          {formatDate(effectiveDate)}
                        </div>
                      ) : row.invalidLectureTopic ? (
                        <span className="syllabus-invalid-lecture-text">
                          N/A
                        </span>
                      ) : (
                        <span className="syllabus-missing-date-placeholder"></span>
                      )}
                    </div>
                    {!row.invalidLectureTopic && !effectiveDate && editingCell?.stableId === sid && (
                      <div className="syllabus-temp-date-hint">
                        Enter a lecture date
                      </div>
                    )}
                  </td>

                  {/* Lecture Topic */}
                  <td>
                    <div
                      className={classNames('syllabus-topic-cell', {
                        invalid: row.invalidLectureTopic,
                        'is-editing-cell': isEditingTopic,
                        'cell-dirty': isCellDirty(row, 'topic'),
                        'editable-cell': !row.invalidLectureTopic,
                        'temp-missing-date':
                          !!row._tempId && !effectiveTopic && !!effectiveDate && editingCell?.stableId === sid,
                      })}
                      onClick={(e) =>
                        !row.invalidLectureTopic &&
                        handleCellClick(e, row, 'topic')
                      }
                      style={{ minHeight: '35px' }}
                    >
                      {isEditingTopic ? (
                        <input
                          type="text"
                          className="syllabus-input"
                          placeholder="Enter lecture topic"
                          value={editValue}
                          autoFocus
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => {
                            if (skipBlurRef.current) {
                              skipBlurRef.current = false;
                              return;
                            }

                            handleCellBlur(row, 'topic');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setEditingCell(null);
                            if (e.key === 'Enter') {
                              handleCellBlur(row, 'topic');
                              if (!effectiveDueDate) {
                                skipBlurRef.current = true;
                                const sid = getStableId(row);
                                setEditingCell({
                                  stableId: sid,
                                  field: 'dueDate',
                                });
                                setEditValue('');
                              }
                            }
                          }}
                          style={{ minHeight: '35px' }}
                        />
                      ) : row.invalidLectureTopic ? (
                        <InvalidLectureTopicPopover
                          courseId={courseId}
                          row={row}
                          onChange={(updatedRow) => {
                            const updated = rows.map((r) =>
                              r._id === updatedRow._id
                                ? { ...r, ...updatedRow }
                                : r,
                            );
                            onChange(updated);
                          }}
                        />
                      ) : invalidLectureTopicReason ? (
                        <ToolTip
                          toolTipText={
                            invalidLectureTopicReason +
                            " We're not sure what readings would be relevant."
                          }
                          toolTipPosition="top"
                        >
                          <div
                            className="d-flex align-items-center"
                            style={{ minHeight: '35px', padding: '4px 8px' }}
                          >
                            {effectiveTopic}
                          </div>
                        </ToolTip>
                      ) : (
                        <div
                          className="d-flex align-items-center"
                          style={{ minHeight: '35px', padding: '4px 8px' }}
                        >
                          {effectiveTopic}
                        </div>
                      )}
                    </div>{' '}
                    {!row.invalidLectureTopic &&
                      !effectiveTopic &&
                      !!effectiveDate &&
                      editingCell?.stableId === sid && (
                        <div className="syllabus-temp-date-hint">
                          Enter a lecture topic
                        </div>
                      )}
                  </td>

                  {/* Due Date */}
                  <td>
                    <div
                      className={classNames('syllabus-due-date-cell', {
                        'is-editing-cell': isEditingDueDate,
                        'cell-dirty': isCellDirty(row, 'dueDate'),
                        'editable-cell': !row.invalidLectureTopic,
                        'temp-missing-date':
                          !!row._tempId &&
                          !!effectiveTopic &&
                          !!effectiveDate &&
                          !effectiveDueDate &&
                          editingCell?.stableId === sid,
                      })}
                      style={{ minHeight: '35px' }}
                      onClick={(e) =>
                        !row.invalidLectureTopic &&
                        handleCellClick(e, row, 'dueDate')
                      }
                    >
                      {isEditingDueDate ? (
                        <input
                          type="date"
                          className="syllabus-input"
                          value={editValue}
                          autoFocus
                          onFocus={(e) => e.target.showPicker?.()}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => handleCellBlur(row, 'dueDate')}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setEditingCell(null);
                              setEditValue('');
                            }
                            if (e.key === 'Enter')
                              handleCellBlur(row, 'dueDate');
                          }}
                        />
                      ) : effectiveDueDate || effectiveDate ? (
                        <div
                          className={
                            row.invalidLectureTopic
                              ? 'syllabus-invalid-lecture-text d-flex align-items-center'
                              : 'd-flex align-items-center'
                          }
                          style={{ minHeight: '35px', padding: '4px 8px' }}
                        >
                          {formatDueDate(effectiveDueDate)}
                        </div>
                      ) : row.invalidLectureTopic ? (
                        <span className="syllabus-invalid-lecture-text">
                          N/A
                        </span>
                      ) : (
                        <span className="syllabus-missing-due-date"></span>
                      )}
                    </div>
                    {!row.invalidLectureTopic &&
                      !!effectiveTopic &&
                      !!effectiveDate &&
                      !effectiveDueDate &&
                      editingCell?.stableId === sid && (
                        <div className="syllabus-temp-date-hint">
                          Enter a due date
                        </div>
                      )}
                  </td>

                  {/* Description */}
                  <td>
                    <div
                      className={classNames('syllabus-description-cell', {
                        'is-editing-cell': isEditingDescription,
                        'cell-dirty': isCellDirty(row, 'description'),
                        'editable-cell': true,
                      })}
                      onClick={(e) => {
                        if (
                          effectiveDescription.length >
                          DESCRIPTION_INLINE_EDIT_LIMIT
                        ) {
                          e.stopPropagation();
                          openDescriptionModal(originalIndex);
                        } else {
                          handleCellClick(e, row, 'description');
                        }
                      }}
                      style={{ minHeight: '35px' }}
                    >
                      {isEditingDescription ? (
                        <textarea
                          className="syllabus-input syllabus-textarea"
                          placeholder="Enter description"
                          value={editValue}
                          autoFocus
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => handleCellBlur(row, 'description')}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setEditingCell(null);
                          }}
                        />
                      ) : (
                        <>
                          <span
                            className="syllabus-description-preview"
                            title={effectiveDescription}
                          >
                            {getDescriptionPreview(effectiveDescription)}
                            {effectiveDescription.length >
                              DESCRIPTION_PREVIEW_LIMIT && (
                              <button
                                type="button"
                                className="syllabus-link-button more-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDescriptionModal(originalIndex);
                                }}
                              >
                                More
                              </button>
                            )}
                          </span>
                          {row.invalidLectureTopicDescription && (
                            <div className="mt-2">
                              {row.invalidLectureTopicDescription}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="syllabus-actions-cell">
                    <div
                      className="d-flex align-items-start"
                      style={{ gap: '8px' }}
                    >
                      <button
                        className="btn-av-link delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteRow(originalIndex);
                        }}
                        title="Delete"
                      >
                        <Icon icon={ICONS.DELETE} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td>
                <ActionLink
                  label="+ Add a Topic"
                  onClick={addRow}
                  disabled={rows.some(
                    (r) =>
                      r._tempId &&
                      r.checkValidity &&
                      (!getEffectiveValue(r, 'topic') ||
                        !getEffectiveValue(r, 'date')),
                  )}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {hasInvalidLectureTopics && (
        <div className="syllabus-warning-message">
          Please review the lectures highlighted in yellow. We're not sure if
          these topics require readings.
        </div>
      )}
    </>
  );
});

export default SyllabusScheduleTable;
